import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { GenericService } from '../../generic.service';
import { FormsModule } from '@angular/forms';
import { LandingService } from '../../landing-page/service/landing.service';
import { TroubleShootEhtService } from '../service/trouble-shoot-eht.service';
import {
  MachineIssuelistItem,
  TroubleshootingImage,
} from '../interface/troubleshoot-eht.interface';
import { HeaderComponent } from '../../shared/header/header.component';
import { defaultLanguage } from '../../shared/constants/language.constants';
import { CrowdinService } from '../../shared/service/crowdin.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-steps-eht',
    imports: [CommonModule, RouterModule, FormsModule, HeaderComponent],
    templateUrl: './steps-eht.component.html',
    styleUrls: ['./steps-eht.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default
})
export class StepsEhtComponent {
  stepId: string | null = '';
  step: any;
  stepsList: any;
  currentStepIndex: number = 0;
  issueName: string | undefined;
  deviceData: any = {};
  deviceData$: any = null;
  popupDetails: {
    showPopup: boolean;
    isImage: boolean;
    url?: string;
    images: TroubleshootingImage[];
  } = {
    showPopup: false,
    isImage: true,
    url: '',
    images: [],
  };

  popupState: {
    show: boolean;
    isDo: boolean;
    message: string;
  } = {
    show: false,
    isDo: false,
    message: '',
  };
  disMachineDetail: any;
  machineIssuelist: MachineIssuelistItem[] = [];
  accountNumber: string = '';

  popupImageIndex: number = 0;
  initialImagesCount: number = 2;
  showSingleImageFromIndex: number = 2;

  showRemoveObstructionMessage: boolean = false;
  currentQuestion: MachineIssuelistItem | null = null;
  totalDots: number[] = [0, 1, 2, 3];
  currentDotIndex: number = 0;
  languageIndex: number = 0;
  translations: { [key: string]: string } = {};
  currentLanguage: string = defaultLanguage;
  languageOptions: Array<{ value: string }> = [];
  isLoadingTranslations = true;
  subs = new Subscription();

  constructor(
    private troubleShootService: TroubleShootEhtService,
    private route: ActivatedRoute,
    private router: Router,
    private genericService: GenericService,
    private landingService: LandingService,
    private crowdinService: CrowdinService,
  ) {
    this.deviceData$ = this.genericService.deviceData.subscribe((data) => {
      if (data) {
        this.deviceData = data;
      }
    });
  }

  ngOnInit() {
    this.languageOptions = this.landingService.getLanguageOptions();

    const stored = localStorage.getItem('deviceData');
    const selectedName = stored
      ? JSON.parse(stored).languageSelected || defaultLanguage
      : defaultLanguage;
    this.currentLanguage = selectedName;
    this.languageIndex = this.crowdinService.getLanguageIndexByName(
      this.currentLanguage,
    );

    this.subs.add(
      this.landingService.languageName$.subscribe((langName) => {
        this.currentLanguage = langName;
        this.languageIndex =
          this.crowdinService.getLanguageIndexByName(langName);
        this.translations = this.crowdinService.getAllTranslations();
      }),
    );

    this.landingService.loadTranslations(this.currentLanguage);
    this.stepId = this.route.snapshot.paramMap.get('stepId');

    if (this.stepId) {
      this.step = this.troubleShootService.getTroubleShootStep(this.stepId);

      if (this.step) {
        this.issueName = this.troubleShootService.getIssuename();
      } else {
        console.error(`No step found for ID: ${this.stepId}`);
      }

      this.stepsList = this.troubleShootService.getStepState();

      if (this.stepsList && this.stepsList.length) {
        this.currentStepIndex = this.troubleShootService.getStepIndex(
          this.stepId,
        );
      }

      this.loadMachineIssuelist();
    }
  }

  async languageChange(event: any) {
    const selectedLang = event.target.value;
    await this.landingService.loadTranslations(selectedLang);
  }

  resetIssuelist() {
    this.machineIssuelist.forEach((question) => {
      question.status = undefined;
      question.answered = false;
      question.expanded = false;
    });
  }

  loadMachineIssuelist() {
    if (
      !this.step ||
      !this.step.machineIssuelistItems ||
      this.step.machineIssuelistItems.length === 0
    ) {
      return;
    }

    this.machineIssuelist = [...this.step.machineIssuelistItems];
    this.resetIssuelist();
  }

  selection(option: any) {
    this.resetIssuelist();
    this.machineIssuelist = [];
    if (option) {
      if (option.key === 'eht-leaking') {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';

        const originalUrl: string = this.router.url;
        const parts: string[] = originalUrl.split('/');
        const lastPart: string | undefined = parts.pop();
        if (lastPart) {
          const replacedUrl: string = originalUrl.replace(
            lastPart,
            option.steps[0].key,
          );
          this.router.navigateByUrl(replacedUrl).then(() => {
            this.step = this.troubleShootService.getTroubleShootStep(
              option.steps[0].key,
            );
            const issueListStep = this.step?.steps?.find(
              (s: { key: string }) => s.key === 'eht-issueList-items',
            );

            if (issueListStep && issueListStep.machineIssuelistItems?.length) {
              this.machineIssuelist = issueListStep.machineIssuelistItems;
            } else {
              this.machineIssuelist = [];
            }
          });
        }
        return;
      }

      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.onSameUrlNavigation = 'reload';
      const originalUrl: string = this.router.url;
      const parts: string[] = originalUrl.split('/');
      const lastPart: string | undefined = parts.pop();
      if (lastPart) {
        const replacedUrl: string = originalUrl.replace(
          lastPart,
          option.steps[0].key,
        );
        this.router.navigateByUrl(replacedUrl).then(() => {
          this.step = this.troubleShootService.getTroubleShootStep(
            option.steps[0].key,
          );
          const issueListStep = this.step.steps?.find(
            (s: { key: string }) => s.key === 'eht-issueList-items',
          );

          if (issueListStep && issueListStep.machineIssuelistItems) {
            this.machineIssuelist = issueListStep.machineIssuelistItems;
          } else {
            this.machineIssuelist = [];
          }
        });
      }
    }
  }

  next() {
    this.currentStepIndex += 1;

    if (this.currentStepIndex < this.stepsList.length) {
      const nextStep = this.stepsList[this.currentStepIndex];
      if (nextStep?.key) {
        this.resetIssuelist();
        this.router.navigate([nextStep.key]);
        this.loadMachineIssuelistForStep(nextStep);
      }
    }
  }

  loadMachineIssuelistForStep(step: any) {
    const issueListStep = step.steps?.find(
      (s: { key: string }) => s.key === 'eht-issueList-items',
    );
    this.machineIssuelist = issueListStep
      ? issueListStep.machineIssuelistItems
      : [];
    this.resetIssuelist();
  }

  getDishmanchineDetails(): void {
    let modelNumber = localStorage.getItem('modelNumber');
    if (modelNumber) {
      this.landingService.getDishMachineDetailsV2().subscribe((disMachine) => {
        this.disMachineDetail = disMachine;
        this.disMachineDetail = disMachine[modelNumber];
      });
    }
  }

  answer(index: number, status: string) {
    this.machineIssuelist[index].status = status;
    this.machineIssuelist[index].answered = true;
    if (this.machineIssuelist[index].details) {
      this.machineIssuelist[index].expanded = true;
    }
  }

  toggleAccordion(index: number) {
    this.machineIssuelist[index].expanded =
      !this.machineIssuelist[index].expanded;
  }

  start(option: any) {
    option = this.stepsList[1];

    if (option?.key) {
      let key = option?.key;

      this.router.routeReuseStrategy.shouldReuseRoute = function () {
        return false;
      };
      this.router.onSameUrlNavigation = 'reload';

      const originalUrl: string = this.router.url;
      const parts: string[] = originalUrl.split('/');
      const lastPart: string | undefined = parts.pop();
      if (lastPart) {
        const replacedUrl: string = originalUrl.replace(lastPart, key);
        this.router.navigateByUrl(replacedUrl);
      }
    }
  }

  resolved() {
    this.router.navigateByUrl('thankYou');
  }

  needHelp() {
    this.landingService.needHelp();
  }

  openImage(imageUrl: string) {
    this.popupDetails.isImage = true;
    this.popupDetails.url = imageUrl;
    this.popupDetails.showPopup = true;
  }

  openVideo(videoUrl: string) {
    this.popupDetails.isImage = false;
    this.popupDetails.url = videoUrl;
    this.popupDetails.showPopup = true;
  }

  requestHelp(option: any) {
    const accountNumber = this.deviceData?.accountNumber || this.accountNumber;
    this.deviceData['issueName'] = this.issueName;
    this.deviceData['languageSelected'] = this.currentLanguage;
    this.genericService.setLocalStorage(this.deviceData);
    this.genericService.deviceData.next(this.deviceData);
    this.router.navigate([`/esrSupport/${accountNumber}`]);
  }

  getTranslatedTitle(item: any) {
    const key = this.currentLanguage.toLowerCase();
    return item.title[key];
  }

  getTranslatedDetails(item: any) {
    const key = this.currentLanguage.toLowerCase();
    return item.details[key];
  }

  firstImage(step: any): TroubleshootingImage | undefined {
    if (step?.images && step.images.length > 0) {
      return step.images[0];
    }
    return undefined;
  }

  openImagesPopup(question: MachineIssuelistItem) {
    this.currentQuestion = question;
    if (question?.details?.images && question.details.images.length > 0) {
      this.popupDetails = {
        showPopup: true,
        isImage: true,
        url: '',
        images:
          question.details.images?.map((img) => ({
            url: img.url,
            alt: 'Image',
          })) || [],
      };
      this.popupState = {
        show: true,
        isDo: false,
        message: '',
      };
      this.popupImageIndex = 0;
      this.updateDotIndicator();
      this.showRemoveObstructionMessage = false;
    }
  }

  getCurrentImages() {
    const images = this.popupDetails.images || [];
    if (this.currentDotIndex === 0) {
      return images.slice(0, 2);
    } else {
      const index = this.currentDotIndex + 1;
      if (index < images.length) {
        return [images[index]];
      } else {
        return [];
      }
    }
  }

  onNextImages() {
    this.popupImageIndex = this.landingService.moveToNextImage(
      this.popupDetails,
      this.popupImageIndex,
    );
    this.currentDotIndex = this.landingService.updateDotIndicator(
      this.popupDetails,
      this.popupImageIndex,
    );
  }

  onPreviousImages() {
    this.popupImageIndex = this.landingService.moveToPreviousImage(
      this.popupDetails,
      this.popupImageIndex,
    );
    this.currentDotIndex = this.landingService.updateDotIndicator(
      this.popupDetails,
      this.popupImageIndex,
    );
  }

  updateDotIndicator() {
    this.currentDotIndex = this.landingService.updateDotIndicator(
      this.popupDetails,
      this.popupImageIndex,
    );
  }

  onDotClick(index: number) {
    this.popupImageIndex = index === 0 ? 0 : index + 1;
    this.updateDotIndicator();
  }

  onSecondImageClick() {
    this.popupState.isDo = true;
    this.popupState.message =
      this.translations['remove_obstruction'] || 'Remove obstruction';
    this.showRemoveObstructionMessage = true;
  }

  closePopup() {
    this.popupDetails.showPopup = false;
    this.popupDetails.images = [];
    this.popupState = {
      show: false,
      isDo: false,
      message: '',
    };
    this.popupImageIndex = 0;
    this.showRemoveObstructionMessage = false;
  }

  isNotDrainingQuestion(question: MachineIssuelistItem): boolean {
    return (
      question?.details?.images?.some((img: { url: string }) =>
        img.url.includes('eht-not-drain'),
      ) ?? false
    );
  }

  isSprayArmQuestion(question: MachineIssuelistItem): boolean {
    return (
      question?.details?.images?.some((img: { url: string }) =>
        img.url.includes('eht-spray-nozzle'),
      ) ?? false
    );
  }

  isSprayArmOrNotDrainingQuestion(question: any): boolean {
    return (
      this.isSprayArmQuestion(question) || this.isNotDrainingQuestion(question)
    );
  }

  get progress() {
    const answered = this.machineIssuelist.filter(
      (item) => item.answered,
    ).length;
    return (answered / this.machineIssuelist.length) * 100;
  }
}
